'use memo';

import { CoolingOffCondition, WaiverOfCoolingOff } from '@urbanx/agx.campaigns.client';
import { AgxRadioGroup, AgxSpacer, AgxTextInput, AgxTypography } from '@urbanx/agx.ui-components2';
import { TaskModalPageProps } from 'pages/allTasks/TaskModal/TaskModalPageProps';
import { useEffect, useState } from 'react';
import { COOLING_OFF_PERIOD_STEP_NAME } from './ContractComplianceHelper';

const MAX_COOLING_OFF_DAYS = 5;
const NO_COOLING_OFF = 'NoCoolingOff';
const FIVE_BUSINESS_DAYS = 'FiveBusinessDays';

type CoolingOffComplianceOption =
  | typeof NO_COOLING_OFF
  | typeof FIVE_BUSINESS_DAYS
  | CoolingOffCondition.IncludeShorteningOfCoolingOff;

interface ContractComplianceCoolingOffPeriodProps extends TaskModalPageProps {
  waiverOfCoolingOff?: WaiverOfCoolingOff;
  onWaiverOfCoolingOffUpdated?: (waiverOfCoolingOff: WaiverOfCoolingOff | undefined) => void;
}

const getCoolingOffOption = (
  waiverOfCoolingOff: WaiverOfCoolingOff | undefined
): CoolingOffComplianceOption => {
  if (waiverOfCoolingOff?.coolingOffCondition === CoolingOffCondition.IncludeWaiverOfCoolingOff) {
    return NO_COOLING_OFF;
  }

  if (
    waiverOfCoolingOff?.coolingOffCondition === CoolingOffCondition.IncludeShorteningOfCoolingOff
  ) {
    return CoolingOffCondition.IncludeShorteningOfCoolingOff;
  }

  return FIVE_BUSINESS_DAYS;
};

const getCoolingOffDaysError = (
  selectedOption: CoolingOffComplianceOption,
  selectedDays: number | undefined
) => {
  if (selectedOption !== CoolingOffCondition.IncludeShorteningOfCoolingOff) {
    return undefined;
  }

  if (selectedDays === undefined) return 'Enter number of days';
  if (selectedDays <= 0) return 'Number of days must be greater than 0';
  if (selectedDays >= MAX_COOLING_OFF_DAYS) {
    return `Number of days must be less than ${MAX_COOLING_OFF_DAYS}`;
  }

  return undefined;
};

const ContractComplianceCoolingOffPeriod = ({
  onWaiverOfCoolingOffUpdated,
  task,
  toggleTaskStep,
  waiverOfCoolingOff,
}: ContractComplianceCoolingOffPeriodProps) => {
  const step = task.taskSteps?.find((taskStep) => taskStep.name === COOLING_OFF_PERIOD_STEP_NAME);
  const [selectedOption, setSelectedOption] = useState<CoolingOffComplianceOption>(
    getCoolingOffOption(waiverOfCoolingOff)
  );
  const [selectedDays, setSelectedDays] = useState<number | undefined>(
    waiverOfCoolingOff?.shortenCoolingOffDays
  );

  useEffect(() => {
    setSelectedOption(getCoolingOffOption(waiverOfCoolingOff));
    setSelectedDays(waiverOfCoolingOff?.shortenCoolingOffDays);
  }, [waiverOfCoolingOff]);

  const daysError = getCoolingOffDaysError(selectedOption, selectedDays);
  const isValid = !daysError;

  useEffect(() => {
    if (!step?.order) return;

    if ((step.isCompleted ?? false) !== isValid) {
      toggleTaskStep(step.order, isValid);
    }
  }, [isValid, step?.isCompleted, step?.order, toggleTaskStep]);

  const updateWaiverOfCoolingOff = (
    option: CoolingOffComplianceOption,
    days: number | undefined
  ) => {
    // The compliance labels show the actual cooling-off period, while the
    // stored value matches the QLD contract request waiver/shortening choice.
    if (option === FIVE_BUSINESS_DAYS) {
      onWaiverOfCoolingOffUpdated?.({});
      return;
    }

    if (option === NO_COOLING_OFF) {
      onWaiverOfCoolingOffUpdated?.({
        coolingOffCondition: CoolingOffCondition.IncludeWaiverOfCoolingOff,
      });
      return;
    }

    onWaiverOfCoolingOffUpdated?.({
      coolingOffCondition: CoolingOffCondition.IncludeShorteningOfCoolingOff,
      shortenCoolingOffDays: days,
    });
  };

  return (
    <AgxSpacer direction="vertical" gap="spacing-md" fill>
      <AgxRadioGroup
        key={selectedOption}
        id="contract-compliance-cooling-off-period"
        label="Cooling off period"
        layout="vertical"
        value={selectedOption}
        options={[
          {
            label: '5 business days',
            value: FIVE_BUSINESS_DAYS,
          },
          {
            label: '_ business day(s)',
            value: CoolingOffCondition.IncludeShorteningOfCoolingOff,
          },
          {
            label: 'No cooling off',
            value: NO_COOLING_OFF,
          },
        ]}
        onChange={(value) => {
          const updatedOption = value as CoolingOffComplianceOption;

          setSelectedOption(updatedOption);
          updateWaiverOfCoolingOff(updatedOption, selectedDays);
        }}
        subContent={
          selectedOption === CoolingOffCondition.IncludeShorteningOfCoolingOff ? (
            <AgxSpacer direction="horizontal" gap="spacing-sm" padding="spacing-2xl">
              <AgxSpacer style={{ width: '90px' }}>
                <AgxTextInput
                  id="contract-compliance-cooling-off-days"
                  placeholder="__"
                  type="number"
                  hideOptionalText
                  maxLength={1}
                  noHeader
                  noDecimalPoints
                  defaultValue={selectedDays?.toString() || ''}
                  errorText={daysError}
                  onChange={(value) => {
                    const parsedDays = parseInt(value, 10);
                    const updatedDays = Number.isNaN(parsedDays) ? undefined : parsedDays;

                    setSelectedDays(updatedDays);
                    updateWaiverOfCoolingOff(selectedOption, updatedDays);
                  }}
                  required
                  stretch
                />
              </AgxSpacer>
              <AgxTypography
                id="contract-compliance-cooling-off-days-label"
                type="body_ui"
                size="medium"
                color="var(--text-primary)"
              >
                day(s) (less than 5)
              </AgxTypography>
            </AgxSpacer>
          ) : undefined
        }
      />
    </AgxSpacer>
  );
};

export default ContractComplianceCoolingOffPeriod;
